import { FilterQueryType } from "cloud-core/filters/FilterSet";
import { AnalyticsStateView } from "../../../store/analytics/Analytics";
import { isInRange } from "../../../utilities/helpers";
import DateTimeFilter from "../../../views/viz/components/Filtering/DateTimeFilter";
import { Entity } from "../Entity";
import { DateEntityProperty } from "../entityProperties/DateEntityProperty";
import { EntityFilter } from "./EntityFilter";

export type DateTimeEntityFilterType = "before" | "after" | "within";
export type DateTimeEntityFilterParameter = { date: Date; type: DateTimeEntityFilterType; withinMilliseconds?: number };

export class DateTimeEntityFilter extends EntityFilter {
    type: FilterQueryType = "time";
    propertyKey: string;

    constructor(displayName: string, filterRef: string, propertyKey: string) {
        super(displayName, filterRef);
        this.propertyKey = propertyKey;
    }

    predicate(entities: Entity[], params: DateTimeEntityFilterParameter): Entity[] {
        return entities.filter((e) => {
            const entityDate = (e.properties[this.propertyKey] as DateEntityProperty)?.value().valueOf();

            if (entityDate == undefined) {
                return false;
            }

            const paramMilliseconds = params.date.valueOf();

            switch (params.type) {
                case "before":
                    return entityDate < paramMilliseconds;
                case "after":
                    return entityDate > paramMilliseconds;
                case "within":
                    if (!params.withinMilliseconds) {
                        console.error(
                            "The parameter 'withinMilliseconds' for the DateTime entity filter is invalid. Could not filter.",
                        );
                        return false;
                    }

                    return isInRange(entityDate, [
                        paramMilliseconds - params.withinMilliseconds,
                        paramMilliseconds + params.withinMilliseconds,
                    ]);
            }
        });
    }

    createComponent(filterId: string, view: AnalyticsStateView, options?: unknown): JSX.Element {
        return <DateTimeFilter filterId={filterId} viewId={view.viewId} />;
    }

    createParameterObject(parameter: DateTimeEntityFilterParameter): unknown {
        return {
            datetime: parameter.date.toISOString(),
            operator: parameter.type,
            withinMs: parameter.withinMilliseconds,
        };
    }

    parseParameterObject(parameterObject: unknown): DateTimeEntityFilterParameter {
        const obj = parameterObject as { datetime: string; operator: DateTimeEntityFilterType; withinMs?: number };
        return {
            date: new Date(obj.datetime),
            type: obj.operator,
            withinMilliseconds: obj.withinMs,
        };
    }
}
